import React, { MouseEvent, useCallback } from 'react';
import { Button, Box, FormErrorMessage, FormControl, Flex } from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { QuizFormData } from '../../types';
import Icon from '@/components/Icon';

export const LeftSectionsFieldArray = () => {
    const {
        formState: { errors },
        control,
        watch,
        getValues,
    } = useFormContext<QuizFormData>();

    const { fields, append, replace } = useFieldArray<QuizFormData, 'activities'>({
        control,
        name: 'activities',
    });

    const activities = watch('activities') ?? [];

    const handleAddSection = useCallback(
        (e: MouseEvent) => {
            e.preventDefault();

            append({
                originId: null,
                title: `Section ${fields.length + 1}`,
                sortIndex: fields.length + 1,
                isSelect: false,
                items: [],
            });
        },
        [append, fields.length]
    );

    const handleRemoveSection = useCallback(
        (index: number) => (e: MouseEvent) => {
            e.preventDefault();

            const list = activities.filter((_, key) => key !== index);

            if (list.some((item) => item.isSelect)) {
                const newList = list.map((item, key) => ({ ...item, sortIndex: key + 1 }));

                replace(newList);
            } else {
                const newList = list.map((item, key) => {
                    if (key === 0) {
                        return {
                            ...item,
                            sortIndex: key + 1,
                            isSelect: true,
                        };
                    }
                    return { ...item, sortIndex: key + 1 };
                });

                replace(newList);
            }
        },
        [replace, activities]
    );

    /** Use array index (not sortIndex) so selection stays in sync with form state; `fields` entries can omit `isSelect`. */
    const handleSelect = useCallback(
        (activityIndex: number) => (e: MouseEvent) => {
            e.preventDefault();

            const current = getValues('activities') ?? [];
            if (!current[activityIndex] || current[activityIndex].isSelect) {
                return;
            }

            replace(
                current.map((activity, i) => ({
                    ...activity,
                    isSelect: i === activityIndex,
                    sortIndex: i + 1,
                }))
            );
        },
        [replace, getValues]
    );

    return (
        <>
            <Flex
                justify="space-between"
                flexDir="row"
                mt="30px"
                mb="20px"
                pb="7px"
                fontSize="15px"
                fontWeight="500"
                color="primary.greyText"
                lineHeight="15px"
                borderBottomStyle="solid"
                borderBottomWidth="1px"
                borderBottomColor="primary.greyLine">
                <Box lineHeight="15px" h="auto">
                    Sections
                </Box>
                <Button
                    h="auto"
                    display="block"
                    color="primary.blue"
                    p="0"
                    fontSize="12px"
                    lineHeight="12px"
                    fontWeight="normal"
                    bg="primary.grey"
                    _hover={{}}
                    _focus={{ outline: 0 }}
                    _active={{}}
                    onClick={handleAddSection}>
                    <Icon icon="circle-plus" fontSize="20px" />
                </Button>
            </Flex>
            <Flex
                flexDir="column"
                mb="36px"
                fontSize="15px"
                fontWeight="500"
                color="primary.greyText"
                lineHeight="15px"
                borderBottomStyle="dashed"
                borderBottomWidth="1px"
                borderBottomColor="primary.greyLine">
                <Droppable droppableId="activities" type="ACTIVITIES">
                    {(provided) => (
                        <div ref={provided.innerRef}>
                            {fields.map((item, index) => (
                                <Draggable key={item.id} draggableId={item.id} index={index}>
                                    {(providedInner) => (
                                        <Flex
                                            ref={providedInner.innerRef}
                                            {...providedInner.draggableProps}
                                            pb="20px"
                                            justify="space-between">
                                            <Box {...providedInner.dragHandleProps}>
                                                <Icon icon="grip-lines" fontSize="15px" />
                                            </Box>

                                            <FormControl isInvalid={errors.hasOwnProperty(`activities.${index}.title`)}>
                                                <Button
                                                    h="auto"
                                                    w="100%"
                                                    display="block"
                                                    textAlign="center"
                                                    bg="primary.grey"
                                                    p="0"
                                                    fontWeight="normal"
                                                    lineHeight="25px"
                                                    color={
                                                        watch(`activities.${index}.isSelect`)
                                                            ? 'primary.blue'
                                                            : 'primary.greyText'
                                                    }
                                                    _hover={{}}
                                                    _focus={{ outline: 0 }}
                                                    _active={{}}
                                                    onClick={handleSelect(index)}>
                                                    {watch(`activities.${index}.title`) ?? ''}
                                                </Button>
                                                <FormErrorMessage>
                                                    {errors.activities?.[index]?.title?.message}
                                                </FormErrorMessage>
                                            </FormControl>

                                            {fields.length > 1 && (
                                                <Button
                                                    h="auto"
                                                    display="block"
                                                    color="red"
                                                    p="0"
                                                    fontSize="12px"
                                                    lineHeight="12px"
                                                    fontWeight="normal"
                                                    bg="primary.grey"
                                                    _hover={{}}
                                                    _focus={{ outline: 0 }}
                                                    _active={{}}
                                                    onClick={handleRemoveSection(index)}>
                                                    <Icon icon="trash-can" fontSize="20px" />
                                                </Button>
                                            )}
                                        </Flex>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </Flex>
        </>
    );
};
